import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';

interface LeaderboardEntry {
  userId: string;
  displayName: string;
  email: string;
  ringerScore: number;
  totalPredictions: number;
  correctPredictions: number;
  accuracy: number;
  totalWagered: number;
  totalEarned: number;
  netProfit: number;
  currentStreak: number;
  longestStreak: number;
  rank: number;
}

const Leaderboard: React.FC = () => {
  const { currentUser } = useAuth();
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<'ringerScore' | 'netProfit' | 'accuracy'>('ringerScore');
  const [timeframe, setTimeframe] = useState<'all' | 'week' | 'month'>('all');
  const [currentUserRank, setCurrentUserRank] = useState<LeaderboardEntry | null>(null);

  useEffect(() => {
    loadLeaderboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy]);

  const loadLeaderboard = async () => {
    setLoading(true);
    try {
      // Load all users
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const userStatsSnapshot = await getDocs(collection(db, 'userStats'));
      const predictionsSnapshot = await getDocs(collection(db, 'predictions'));

      // Create a map of user stats
      const statsMap = new Map();
      userStatsSnapshot.forEach(doc => {
        statsMap.set(doc.id, doc.data());
      });

      // Calculate prediction stats for each user
      const predictionStats = new Map<string, {
        totalWagered: number;
        totalEarned: number;
        netProfit: number;
        totalPredictions: number;
        wonPredictions: number;
      }>();

      predictionsSnapshot.forEach(doc => {
        const prediction = doc.data();
        const userId = prediction.userId;
        
        if (!predictionStats.has(userId)) {
          predictionStats.set(userId, {
            totalWagered: 0,
            totalEarned: 0,
            netProfit: 0,
            totalPredictions: 0,
            wonPredictions: 0
          });
        }

        const stats = predictionStats.get(userId)!;
        stats.totalPredictions++;
        stats.totalWagered += prediction.wagerAmount || 0;

        if (prediction.status === 'won') {
          stats.totalEarned += prediction.ringersEarned || 0;
          stats.netProfit += (prediction.ringersEarned || 0) - (prediction.wagerAmount || 0);
          stats.wonPredictions++;
        } else if (prediction.status === 'lost') {
          stats.netProfit -= prediction.wagerAmount || 0;
        }
      });

      // Build leaderboard entries
      const entries: LeaderboardEntry[] = [];
      
      usersSnapshot.forEach(doc => {
        const userData = doc.data();
        const userId = doc.id;
        
        // Only include Ringers (not auctioneers or admins)
        if (userData.role !== 'ringer') return;

        const stats = statsMap.get(userId) || {};
        const predStats = predictionStats.get(userId) || {
          totalWagered: 0,
          totalEarned: 0,
          netProfit: 0,
          totalPredictions: 0,
          wonPredictions: 0
        };

        const accuracy = predStats.totalPredictions > 0 
          ? (predStats.wonPredictions / predStats.totalPredictions) * 100 
          : 0;

        entries.push({
          userId,
          displayName: userData.displayName || 'Anonymous',
          email: userData.email || '',
          ringerScore: stats.ringerScore || 0,
          totalPredictions: predStats.totalPredictions,
          correctPredictions: predStats.wonPredictions,
          accuracy,
          totalWagered: predStats.totalWagered,
          totalEarned: predStats.totalEarned,
          netProfit: predStats.netProfit,
          currentStreak: stats.currentStreak || 0,
          longestStreak: stats.longestStreak || 0,
          rank: 0
        });
      });

      // Sort based on selected criteria
      entries.sort((a, b) => {
        switch (sortBy) {
          case 'ringerScore':
            return b.ringerScore - a.ringerScore;
          case 'netProfit':
            return b.netProfit - a.netProfit;
          case 'accuracy':
            // Only compare users with at least 5 predictions
            if (a.totalPredictions < 5) return 1;
            if (b.totalPredictions < 5) return -1;
            return b.accuracy - a.accuracy;
          default:
            return b.ringerScore - a.ringerScore;
        }
      });

      // Assign ranks
      entries.forEach((entry, index) => {
        entry.rank = index + 1;
      });

      setLeaderboard(entries);

      // Find current user's rank
      if (currentUser) {
        const userEntry = entries.find(e => e.userId === currentUser.uid);
        setCurrentUserRank(userEntry || null);
      }

    } catch (error) {
      console.error('Error loading leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankBadge = (rank: number) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  const getRankColor = (rank: number) => {
    if (rank === 1) return 'text-yellow-600';
    if (rank === 2) return 'text-gray-500';
    if (rank === 3) return 'text-orange-600';
    return 'text-gray-700';
  };

  const getSortLabel = () => {
    switch (sortBy) {
      case 'ringerScore':
        return 'Ringer Score';
      case 'netProfit':
        return 'Net Profit';
      case 'accuracy':
        return 'Win Rate';
      default:
        return 'Ringer Score';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading leaderboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Leaderboard</h1>
          <p className="text-gray-600 mt-1">See how you stack up against other Ringers</p>
        </div>
      </div>

      {/* Current User Rank Card */}
      {currentUserRank && (
        <div className="card bg-gradient-to-r from-primary-600 to-purple-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Your Rank</p>
              <p className="text-4xl font-bold">{getRankBadge(currentUserRank.rank)}</p>
            </div>
            <div className="text-right">
              <p className="text-sm opacity-90">{getSortLabel()}</p>
              <p className="text-3xl font-bold">
                {sortBy === 'ringerScore' && currentUserRank.ringerScore}
                {sortBy === 'netProfit' && `${currentUserRank.netProfit >= 0 ? '+' : ''}${currentUserRank.netProfit} R$`}
                {sortBy === 'accuracy' && `${currentUserRank.accuracy.toFixed(1)}%`}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm opacity-90">Total Predictions</p>
              <p className="text-3xl font-bold">{currentUserRank.totalPredictions}</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* Sort By */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
            <div className="flex gap-2">
              <button
                onClick={() => setSortBy('ringerScore')}
                className={`px-4 py-2 rounded-lg font-semibold ${
                  sortBy === 'ringerScore'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Ringer Score
              </button>
              <button
                onClick={() => setSortBy('netProfit')}
                className={`px-4 py-2 rounded-lg font-semibold ${
                  sortBy === 'netProfit'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Net Profit
              </button>
              <button
                onClick={() => setSortBy('accuracy')}
                className={`px-4 py-2 rounded-lg font-semibold ${
                  sortBy === 'accuracy'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Win Rate
              </button>
            </div>
          </div>

          {/* Timeframe - Placeholder for future */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Timeframe</label>
            <div className="flex gap-2">
              <button
                onClick={() => setTimeframe('all')}
                className={`px-4 py-2 rounded-lg font-semibold ${
                  timeframe === 'all'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All Time
              </button>
              <button
                onClick={() => setTimeframe('month')}
                className="px-4 py-2 rounded-lg font-semibold bg-gray-100 text-gray-400 cursor-not-allowed"
                disabled
              >
                This Month
              </button>
              <button
                onClick={() => setTimeframe('week')}
                className="px-4 py-2 rounded-lg font-semibold bg-gray-100 text-gray-400 cursor-not-allowed"
                disabled
              >
                This Week
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Top 3 Podium */}
      {leaderboard.length >= 3 && (
        <div className="card">
          <h2 className="text-2xl font-bold mb-6 text-center">Top Ringers</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {/* 2nd Place */}
            <div className="order-2 md:order-1 text-center">
              <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg p-6 border-2 border-gray-300">
                <div className="text-5xl mb-2">🥈</div>
                <p className="font-bold text-lg">{leaderboard[1].displayName}</p>
                <p className="text-sm text-gray-600 mb-3">
                  {leaderboard[1].totalPredictions} predictions
                </p>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Ringer Score:</span>
                    <span className="font-bold">{leaderboard[1].ringerScore}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Net Profit:</span>
                    <span className={`font-bold ${leaderboard[1].netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {leaderboard[1].netProfit >= 0 ? '+' : ''}{leaderboard[1].netProfit} R$
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Win Rate:</span>
                    <span className="font-bold">{leaderboard[1].accuracy.toFixed(1)}%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 1st Place */}
            <div className="order-1 md:order-2 text-center">
              <div className="bg-gradient-to-br from-yellow-100 to-yellow-200 rounded-lg p-6 border-4 border-yellow-400 transform md:scale-110">
                <div className="text-6xl mb-2">🥇</div>
                <p className="font-bold text-xl">{leaderboard[0].displayName}</p>
                <p className="text-sm text-gray-600 mb-3">
                  {leaderboard[0].totalPredictions} predictions
                </p>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Ringer Score:</span>
                    <span className="font-bold text-lg">{leaderboard[0].ringerScore}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Net Profit:</span>
                    <span className={`font-bold text-lg ${leaderboard[0].netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {leaderboard[0].netProfit >= 0 ? '+' : ''}{leaderboard[0].netProfit} R$
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Win Rate:</span>
                    <span className="font-bold text-lg">{leaderboard[0].accuracy.toFixed(1)}%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 3rd Place */}
            <div className="order-3 text-center">
              <div className="bg-gradient-to-br from-orange-100 to-orange-200 rounded-lg p-6 border-2 border-orange-300">
                <div className="text-5xl mb-2">🥉</div>
                <p className="font-bold text-lg">{leaderboard[2].displayName}</p>
                <p className="text-sm text-gray-600 mb-3">
                  {leaderboard[2].totalPredictions} predictions
                </p>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Ringer Score:</span>
                    <span className="font-bold">{leaderboard[2].ringerScore}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Net Profit:</span>
                    <span className={`font-bold ${leaderboard[2].netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {leaderboard[2].netProfit >= 0 ? '+' : ''}{leaderboard[2].netProfit} R$
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Win Rate:</span>
                    <span className="font-bold">{leaderboard[2].accuracy.toFixed(1)}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Full Leaderboard Table */}
      <div className="card">
        <h2 className="text-2xl font-bold mb-4">Full Rankings</h2>
        
        {leaderboard.length === 0 ? (
          <p className="text-center text-gray-600 py-8">No rankings available yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Rank</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Ringer</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Score</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Predictions</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Win Rate</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Wagered</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Net Profit</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Streak</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {leaderboard.map((entry) => (
                  <tr 
                    key={entry.userId}
                    className={`hover:bg-gray-50 ${
                      entry.userId === currentUser?.uid ? 'bg-primary-50' : ''
                    }`}
                  >
                    <td className="px-4 py-3">
                      <span className={`text-2xl font-bold ${getRankColor(entry.rank)}`}>
                        {getRankBadge(entry.rank)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-semibold">
                          {entry.displayName}
                          {entry.userId === currentUser?.uid && (
                            <span className="ml-2 text-xs bg-primary-600 text-white px-2 py-1 rounded">You</span>
                          )}
                        </p>
                        {entry.longestStreak >= 5 && (
                          <p className="text-xs text-orange-600">🔥 {entry.longestStreak} longest streak</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="font-bold text-primary-600">{entry.ringerScore}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div>
                        <p className="font-semibold">{entry.totalPredictions}</p>
                        <p className="text-xs text-gray-600">{entry.correctPredictions} won</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`font-semibold ${
                        entry.accuracy >= 60 ? 'text-green-600' : 
                        entry.accuracy >= 40 ? 'text-yellow-600' : 
                        'text-gray-600'
                      }`}>
                        {entry.accuracy.toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-gray-700">{entry.totalWagered} R$</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`font-bold ${
                        entry.netProfit > 0 ? 'text-green-600' : 
                        entry.netProfit < 0 ? 'text-red-600' : 
                        'text-gray-600'
                      }`}>
                        {entry.netProfit >= 0 ? '+' : ''}{entry.netProfit} R$
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="font-semibold">
                        {entry.currentStreak > 0 ? `🔥 ${entry.currentStreak}` : '-'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="card bg-blue-50">
        <h3 className="font-bold mb-2">📊 How Rankings Work</h3>
        <div className="grid md:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="font-semibold text-blue-800">Ringer Score</p>
            <p className="text-blue-700">Composite score based on prediction accuracy, wagering activity, and consistency</p>
          </div>
          <div>
            <p className="font-semibold text-blue-800">Net Profit</p>
            <p className="text-blue-700">Total winnings minus total losses from all predictions</p>
          </div>
          <div>
            <p className="font-semibold text-blue-800">Win Rate</p>
            <p className="text-blue-700">Percentage of predictions that resulted in wins (minimum 5 predictions required)</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;